// Microbe RTS — home defensive turrets.
//
// A turret is a lightweight STRUCTURE (not a Unit): it lives in state.turrets, doesn't move, costs
// no pop, isn't selectable. Unlocked by a standalone Tier-2 mutation; cap = min(3, tier-1). Tap-placed
// around the home rim, >= TURRET.minSepDeg apart. Each turret:
//   • fires a weak ranged shot at the nearest enemy (mirrors sim/defense.ts node shots),
//   • exudes a weak poison cloud (folded into defense.ts's contact-poison pass — see resolveBaseDefense),
//   • has its own HP and is CHIPPED by any hostile unit in range (walk-through, continuous proximity dps).
// Dead (hp<=0) turrets stay in the array as repairable rubble. Construction/repair takes TURRET.buildSeconds
// during which the turret is INERT (no shot, no poison) but still damageable.
//
// Shared helpers: placeTurretAt / repairTurrets are called BOTH by the human command handler (sim.ts)
// and directly by the bot (bot.ts), exactly like buyMutation — one validator, two callers. Deterministic:
// no RNG here; placement geometry is pure.

import type { GameState, Side, Unit, Vec, Colony } from '../types.js';
import { TURRET, BASE_DEFENSE, QUEEN, COMBAT, MUTATIONS } from '../config.js';
import { dist, unitSpec, nodeById } from './world.js';
import { canAfford, pay } from './units.js';
import { cleanupDead } from './combat.js';

/** Does this colony own a mutation that unlocks turrets? */
export function turretsUnlocked(col: Colony): boolean {
  for (const m of MUTATIONS) {
    if (m.effect?.unlockTurrets && (col.genome[m.id] ?? 0) > 0) return true;
  }
  return false;
}

/** Max turrets this colony may field right now (0 if locked; else 1@T2, 2@T3, 3@T4). */
export function turretCap(col: Colony): number {
  if (!turretsUnlocked(col)) return 0;
  return Math.min(3, col.tier - 1);
}

/** Smallest absolute angular difference between two angles (radians), in [0, PI]. */
function angDiff(a: number, b: number): number {
  let d = Math.abs(a - b) % (Math.PI * 2);
  if (d > Math.PI) d = Math.PI * 2 - d;
  return d;
}

/** The home WorldNode for a side (the seeded crater promoted to a Home), or null. */
function homeOf(state: GameState, side: Side) {
  return nodeById(state, state.colonies[side].homeId) ?? null;
}

/**
 * Place one turret for a side at a tapped world point. Validates: unlocked, under cap, >= minSepDeg
 * from every existing turret, affordable. The tap position is CLAMPED to the home ring band, so the
 * turret always sits around the home even if the tap lands far off. Pays TURRET.cost on success.
 * Returns true if a turret was placed. Called by the UI command AND directly by the bot.
 */
export function placeTurretAt(state: GameState, side: Side, pos: Vec): boolean {
  const col = state.colonies[side];
  if (turretsUnlocked(col) === false) return false;
  const cap = turretCap(col);
  const mine = state.turrets.filter((t) => t.side === side);
  if (mine.length >= cap) return false; // live + rubble both occupy a slot
  const home = homeOf(state, side);
  if (!home) return false;

  // Clamp the tapped point to the home ring band (deterministic geometry, no RNG).
  let dx = pos.x - home.pos.x;
  let dy = pos.y - home.pos.y;
  let d = Math.hypot(dx, dy);
  if (d < 1e-3) { dx = 1; dy = 0; d = 1; } // tapped exactly on the core → default east angle
  const minR = home.radius * TURRET.ringMinFrac;
  const maxR = home.radius * TURRET.ringMaxFrac;
  const rr = Math.max(minR, Math.min(maxR, d));
  const ang = Math.atan2(dy, dx);

  // >= minSepDeg from every existing turret (measured as angle off the home centre).
  const minSep = (TURRET.minSepDeg * Math.PI) / 180;
  for (const t of mine) {
    const ta = Math.atan2(t.pos.y - home.pos.y, t.pos.x - home.pos.x);
    if (angDiff(ang, ta) < minSep) return false;
  }

  if (!canAfford(col.res, TURRET.cost)) return false;
  pay(col.res, TURRET.cost);
  state.turrets.push({
    id: `t${state.nextId++}`,
    side,
    pos: { x: home.pos.x + Math.cos(ang) * rr, y: home.pos.y + Math.sin(ang) * rr },
    hp: TURRET.maxHp,
    maxHp: TURRET.maxHp,
    fireCooldown: 0,
    timer: TURRET.buildSeconds, // inert while constructing
  });
  return true;
}

/**
 * Repair ALL of a side's damaged/dead turrets together. Cost = sum over them of TURRET.cost * (missing
 * HP fraction) — a dead turret (hp<=0) costs full price. Puts each into the buildSeconds repair timer
 * (inert while repairing); resolveTurrets restores hp to max when a timer completes. No-op if nothing
 * needs repair or the colony can't afford the total. Returns true on success.
 */
export function repairTurrets(state: GameState, side: Side): boolean {
  const col = state.colonies[side];
  // Only turrets not already mid-repair/build and below full hp (dead included: hp<=0 < maxHp).
  const targets = state.turrets.filter((t) => t.side === side && t.timer <= 0 && t.hp < t.maxHp);
  if (targets.length === 0) return false;
  const cost = { energy: 0, amino: 0, toxin: 0 };
  for (const t of targets) {
    const missing = 1 - Math.max(0, t.hp) / t.maxHp; // dead → 1 (full price)
    cost.energy += TURRET.cost.energy * missing;
    cost.amino += TURRET.cost.amino * missing;
  }
  if (!canAfford(col.res, cost)) return false;
  pay(col.res, cost);
  for (const t of targets) {
    // A rubble turret (hp<=0) comes back as a fragile 1-hp shell WHILE repairing, so an enemy army
    // camped on it can still deny the repair (kill it back to rubble) instead of it reviving
    // untouchable. hp is restored to max only when the timer completes (resolveTurrets).
    if (t.hp <= 0) t.hp = 1;
    t.timer = TURRET.buildSeconds;
  }
  return true;
}

/** Total repair cost for a side's damaged/dead turrets right now (for the HUD chip). */
export function repairCostFor(state: GameState, side: Side): { energy: number; amino: number; toxin: number } {
  const cost = { energy: 0, amino: 0, toxin: 0 };
  for (const t of state.turrets) {
    if (t.side !== side || t.timer > 0 || t.hp >= t.maxHp) continue;
    const missing = 1 - Math.max(0, t.hp) / t.maxHp;
    cost.energy += TURRET.cost.energy * missing;
    cost.amino += TURRET.cost.amino * missing;
  }
  return cost;
}

/**
 * Per-tick turret resolution: build/repair timers, enemy proximity damage (walk-through chip), and the
 * weak ranged shot. Poison auras are handled in sim/defense.ts (resolveBaseDefense) so they reuse the
 * whole envenom system. Runs right after resolveBaseDefense.
 */
export function resolveTurrets(state: GameState, dt: number): void {
  if (state.turrets.length === 0) return;
  let killedUnit = false;

  for (const t of state.turrets) {
    // Construction / repair timer. On completion the turret is restored to full HP + goes active.
    if (t.timer > 0) {
      t.timer = Math.max(0, t.timer - dt);
      if (t.timer <= 0) t.hp = t.maxHp;
    }
    if (t.hp <= 0) continue; // dead rubble (inert) — only a repair timer (above) can revive it

    // Enemy units in range CHIP the turret continuously (walk-through, no collision). Each hostile
    // combat unit within its own attack reach contributes its dps to its nearest turret — here we just
    // sum every hostile in reach (turrets are meant to be ground down by a passing army).
    let incoming = 0;
    for (const u of state.units) {
      if (u.hp <= 0 || u.side === t.side || u.side === 'wild') continue;
      const spec = unitSpec(u.kind);
      if (spec.dmg <= 0) continue; // healers / non-combat don't grind structures
      const reach = spec.range > 0 ? spec.range : u.radius + TURRET.radius + COMBAT.reach;
      if (dist(u.pos, t.pos) <= reach) {
        incoming += (spec.dmg / Math.max(0.0001, spec.interval)) * (u.queen ? QUEEN.dmgMult : 1);
      }
    }
    if (incoming > 0) {
      t.hp -= incoming * dt;
      if (t.hp <= 0) { t.hp = 0; t.timer = 0; continue; } // just destroyed → rubble, stop here
    }

    // Inert while building/repairing: no shot.
    if (t.timer > 0) continue;

    // Weak ranged shot at the nearest enemy in range (mirrors defense.ts node shot).
    t.fireCooldown = Math.max(0, t.fireCooldown - dt);
    if (t.fireCooldown > 0) continue;
    let best: Unit | null = null;
    let bestD = Infinity;
    for (const u of state.units) {
      if (u.hp <= 0 || u.side === t.side || u.side === 'wild') continue;
      const d = dist(u.pos, t.pos);
      if (d <= TURRET.range && d < bestD) { bestD = d; best = u; }
    }
    if (!best) continue;
    best.hp -= Math.max(1, TURRET.shotDmg - unitSpec(best.kind).armor - (best.queen ? QUEEN.armor : 0));
    if (best.hp <= 0) killedUnit = true;
    t.fireCooldown = TURRET.shotInterval;
    state.shots.push({ from: { ...t.pos }, to: { ...best.pos }, ttl: BASE_DEFENSE.shotTtl, side: t.side });
  }

  if (killedUnit) cleanupDead(state);
}
